import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { requireWorkspace } from "../lib/requireWorkspace";
import { session } from "../lib/session";

import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";

export default function BrandDNAPage() {
  const [workspace, setWorkspace] = useState(null);

  const [form, setForm] = useState({
    business_type: "",
    offer: "",
    website_url: "",
    audience: "",
    tone: "",
    differentiator: "",
    compliance_notes: "",
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [notice, setNotice] = useState(null);

  const requiredFields = ["business_type", "offer", "audience", "tone", "differentiator"];
  const completedFields = requiredFields.filter((key) => (form[key] || "").trim().length > 0);
  const completeness = Math.round((completedFields.length / requiredFields.length) * 100);
  const isAdmin = session.isOwnerAdmin();
  const canGenerate = isAdmin || completeness >= 40;
  const statusLabel =
    completeness === 100 ? "Complete" : completeness >= 40 ? "Almost Ready" : "Needs Brand DNA";

  const hasContent = useMemo(() => {
    const v = Object.values(form).join("").trim();
    return v.length > 0;
  }, [form]);

  useEffect(() => {
    try {
      const ws = requireWorkspace();
      setWorkspace(ws);
      loadBrand(ws.id);
    } catch (e) {
      setErr(e.message || "No workspace selected.");
    }
  }, []);

  async function loadBrand(workspaceId) {
    setErr(null);
    setLoading(true);
    try {
      const res = await api.get(`/api/workspaces/${workspaceId}/brand-dna`);
      const data = res.data;
      setForm({
        business_type: data.business_type ?? "",
        offer: data.offer ?? "",
        website_url: data.website_url ?? "",
        audience: data.audience ?? "",
        tone: data.tone ?? "",
        differentiator: data.differentiator ?? "",
        compliance_notes: data.compliance_notes ?? "",
      });
    } catch (e) {
      // 404 is normal if brand DNA not created yet
      if (e?.response?.status !== 404) {
        setErr(e?.response?.data?.detail || "Failed to load Brand DNA.");
      }
    } finally {
      setLoading(false);
    }
  }

  async function save() {
    if (!workspace) {
      setErr("Select a workspace before saving Brand DNA.");
      return;
    }
    setErr(null);
    setNotice(null);
    setSaving(true);
    try {
      const res = await api.post(`/api/workspaces/${workspace.id}/brand-dna`, {
        business_type: form.business_type || null,
        offer: form.offer || null,
        website_url: form.website_url || null,
        audience: form.audience || null,
        tone: form.tone || null,
        differentiator: form.differentiator || null,
        compliance_notes: form.compliance_notes || null,
      });
      const data = res.data || {};
      setForm({
        business_type: data.business_type ?? "",
        offer: data.offer ?? "",
        website_url: data.website_url ?? "",
        audience: data.audience ?? "",
        tone: data.tone ?? "",
        differentiator: data.differentiator ?? "",
        compliance_notes: data.compliance_notes ?? "",
      });
      setSavedAt(new Date());
      setNotice("Brand DNA saved and synced to your AI team.");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to save Brand DNA.");
    } finally {
      setSaving(false);
    }
  }

  if (!workspace) {
    return (
      <div className="space-y-4" data-testid="brand-dna-no-workspace">
        <div className="text-2xl font-semibold">Brand DNA</div>
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-4">
            <div className="text-sm text-muted-foreground">
              {err || "No workspace selected."}
            </div>
            <div className="mt-3">
              <Link to="/app/workspaces">
                <Button>Go to Workspaces</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="brand-dna-page">
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="text-2xl font-semibold">Brand DNA</div>
          <div className="text-sm text-muted-foreground">
            Teach your AI team how to write like you and market your offer consistently.
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Badge variant="secondary">{workspace.name}</Badge>
          <Badge variant={completeness === 100 ? "default" : completeness >= 40 ? "secondary" : "outline"}>
            {statusLabel}
          </Badge>
          <Link to="/app/workspaces" className="text-sm text-muted-foreground hover:underline">
            change
          </Link>
        </div>
      </div>

      {err && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-3 text-sm text-red-700">{err}</CardContent>
        </Card>
      )}

      {notice && (
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="py-3 text-sm text-emerald-700">{notice}</CardContent>
        </Card>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        {/* Left: form */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              Brand details
              {savedAt ? (
                <span className="text-xs text-muted-foreground">
                  Saved {savedAt.toLocaleTimeString()}
                </span>
              ) : (
                <span className="text-xs text-muted-foreground">
                  {loading ? "Loading…" : "Not saved yet"}
                </span>
              )}
            </CardTitle>
          </CardHeader>

          <CardContent className="space-y-4">
            <div
              className={[
                "rounded-lg border p-3 transition",
                completeness === 100 ? "border-emerald-300 bg-emerald-50 shadow-[0_0_24px_rgba(16,185,129,0.25)]" : "bg-background",
              ].join(" ")}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm font-medium">Brand readiness</div>
                <div className="text-sm font-medium">{completeness}%</div>
              </div>
              <div className="mt-2 h-2 overflow-hidden rounded-full bg-muted">
                <div
                  className={[
                    "h-full rounded-full transition-all",
                    completeness === 100 ? "bg-emerald-500" : completeness >= 40 ? "bg-primary" : "bg-amber-500",
                  ].join(" ")}
                  style={{ width: `${completeness}%` }}
                />
              </div>
              {completeness < 40 && (
                <div className="mt-2 text-xs text-amber-700">
                  Add your offer, audience, tone, and differentiator so the AI team has enough context.
                </div>
              )}
              {completeness === 100 && (
                <div className="mt-2 text-xs text-emerald-700">
                  Brand DNA is complete. Your AI team has the context it needs.
                </div>
              )}
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-medium">Business type</label>
                <Input
                  placeholder="e.g., Ecommerce, Local service, Agency"
                  value={form.business_type ?? ""}
                  onChange={(e) => setForm((p) => ({ ...p, business_type: e.target.value }))}
                  data-testid="brand-business-type"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Website URL</label>
                <Input
                  placeholder="https://yourwebsite.com"
                  value={form.website_url ?? ""}
                  onChange={(e) => setForm((p) => ({ ...p, website_url: e.target.value }))}
                  data-testid="brand-website-url"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Offer (what you sell)</label>
              <Textarea
                placeholder="Describe the product/service, price point, and what makes it special."
                value={form.offer ?? ""}
                onChange={(e) => setForm((p) => ({ ...p, offer: e.target.value }))}
                className="min-h-[90px]"
                data-testid="brand-offer"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Target audience</label>
              <Textarea
                placeholder="Who is it for? Their pain points? Their objections?"
                value={form.audience ?? ""}
                onChange={(e) => setForm((p) => ({ ...p, audience: e.target.value }))}
                className="min-h-[90px]"
                data-testid="brand-audience"
              />
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-medium">Tone / Voice</label>
                <Input
                  placeholder="e.g., Trustworthy, warm, confident"
                  value={form.tone ?? ""}
                  onChange={(e) => setForm((p) => ({ ...p, tone: e.target.value }))}
                  data-testid="brand-tone"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Differentiator</label>
                <Input
                  placeholder="What makes you different than competitors?"
                  value={form.differentiator ?? ""}
                  onChange={(e) => setForm((p) => ({ ...p, differentiator: e.target.value }))}
                  data-testid="brand-differentiator"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Compliance notes</label>
              <Textarea
                placeholder="Example: Avoid medical cure claims. No guaranteed results. No income promises."
                value={form.compliance_notes ?? ""}
                onChange={(e) => setForm((p) => ({ ...p, compliance_notes: e.target.value }))}
                className="min-h-[90px]"
                data-testid="brand-compliance"
              />
              <div className="text-xs text-muted-foreground">
                Coach River uses this to flag risky claims before you launch.
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button onClick={save} disabled={saving || loading} data-testid="save-brand-btn">
                {saving ? "Saving…" : savedAt ? "Save Brand DNA again" : "Save Brand DNA"}
              </Button>

              {canGenerate ? (
                <Link to="/app/ads">
                  <Button variant="outline" data-testid="goto-ad-creator-btn">
                    Go to Ad Creator
                  </Button>
                </Link>
              ) : (
                <Button
                  variant="outline"
                  disabled
                  className="opacity-50 cursor-not-allowed"
                  data-testid="goto-ad-creator-btn"
                  title="Complete at least 40% of Brand DNA first."
                >
                  Go to Ad Creator
                </Button>
              )}

              <Link to="/app/workspaces" className="text-sm text-muted-foreground hover:underline">
                Manage workspaces
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Right: guidance */}
        <Card>
          <CardHeader>
            <CardTitle>Your AI Team</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-lg border p-3">
              <div className="font-medium">Maya Metrics</div>
              <div className="text-sm text-muted-foreground">
                Strategy + budget + objective recommendations.
              </div>
            </div>

            <div className="rounded-lg border p-3">
              <div className="font-medium">Cole Creative</div>
              <div className="text-sm text-muted-foreground">
                Hooks, headlines, primary text, UGC scripts, angles.
              </div>
            </div>

            <div className="rounded-lg border p-3">
              <div className="font-medium">Coach River</div>
              <div className="text-sm text-muted-foreground">
                Launch checklist + compliance flags + week-1 plan.
              </div>
            </div>

            <Separator />

            <div className="text-sm text-muted-foreground">
              Pro tip: Add 1–2 competitor notes inside "Differentiator" to get sharper angles.
            </div>

            {canGenerate ? (
              <Link to="/app/ads">
                <Button className="w-full" data-testid="generate-ad-pack-btn">
                  Generate Ad Pack
                </Button>
              </Link>
            ) : (
              <Button
                className="w-full"
                disabled
                title="Complete at least 40% of Brand DNA first."
                data-testid="generate-ad-pack-btn"
              >
                Generate Ad Pack
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
